import { effect, type ReadonlySignal } from '@preact/signals';
import type { TelemetryBatch } from '@vm/shared';
import { api } from '../api/routes';
import { hasSession } from '../api/http';
import { platform } from '../platform';
import { conn } from './store';

/**
 * Fire-and-forget analytics (no personal data), batched every 30 s: gameplay and funnel events, a
 * heartbeat while the app is visible (play session length), and crash reports.
 */
const sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
let pending: TelemetryBatch['events'] = [];
const context: NonNullable<TelemetryBatch['context']> = {
  appVersion: __APP_VERSION__,
  platform,
  locale: navigator.language,
  touch: navigator.maxTouchPoints > 0,
};

export function track(type: string, props?: Record<string, string | number | boolean>): void {
  pending.push({ type, ts: Date.now(), props });
  if (pending.length > 200) pending = pending.slice(-200);
  if (pending.length >= 25) void flush();
}

async function flush(): Promise<void> {
  if (!pending.length || conn.value !== 'online' || !hasSession()) return;
  const events = pending;
  pending = [];
  try {
    await api.events.send({ sessionId, events, context });
  } catch {
    /* analytics are best-effort */
  }
}

setInterval(() => void flush(), 30_000);
addEventListener('pagehide', () => void flush());
// Heartbeats only while visible: the play session's length counts time on screen, not open tabs.
setInterval(() => {
  if (document.visibilityState === 'visible') track('heartbeat');
}, 60_000);

// Crash reports ------------------------------------------------------------------------------------------------

let errorsSent = 0;
const lastSent = new Map<string, number>();
let where = (): string => 'app';

/** Records an uncaught error (at most 25 per app run, the same message at most every 10 s). */
export function reportError(err: unknown, source: string): void {
  const e = err instanceof Error ? err : null;
  const message = (e ? `${e.name}: ${e.message}` : String(err ?? 'Unknown error')).slice(0, 500);
  const now = Date.now();
  if (errorsSent >= 25 || now - (lastSent.get(message) ?? 0) < 10_000) return;
  errorsSent++;
  lastSent.set(message, now);
  track('client_error', { message, stack: (e?.stack ?? '').slice(0, 3800), source, screen: where() });
  void flush();
}

addEventListener('error', (ev) => reportError(ev.error ?? ev.message, 'window'));
addEventListener('unhandledrejection', (ev) => reportError(ev.reason, 'promise'));

/**
 * Screen views, onboarding steps and the error location follow the app's state. Called once by the
 * app shell (the signals live in modules that themselves log events).
 */
export function wireAnalytics(s: { screen: ReadonlySignal<string>; inMatch: ReadonlySignal<boolean>; onboarding: ReadonlySignal<string | null> }): void {
  where = () => (s.inMatch.value ? 'match' : s.onboarding.value ? `onboarding:${s.onboarding.value}` : s.screen.value);
  effect(() => {
    if (!s.inMatch.value && !s.onboarding.value) track('screen', { label: s.screen.value });
  });
  effect(() => {
    const step = s.onboarding.value;
    if (step) track('onboarding', { label: step });
  });
}
